import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { useAuth } from "../../contexts/AuthContext";
import { auth } from '@/firebase';
import logo from "../../assets/image/logo.jpg";
import { getAdminThemeSettings, applyAdminThemeSettingsToDocument } from "@/utils/adminThemeSettings";

type NavItem = {
  path: string;
  label: string;
  icon: string;
};

type NavSection = {
  id: string;
  title: string;
  items: NavItem[];
};

const DASHBOARD_ITEM: NavItem = {
  path: "/admin",
  label: "ダッシュボード",
  icon: "ri-dashboard-line",
};

const NAV_SECTIONS: NavSection[] = [
  {
    id: "crm",
    title: "CRM",
    items: [
      { path: "/admin/orders", label: "注文管理", icon: "ri-shopping-cart-line" },
      { path: "/admin/users", label: "ユーザー管理", icon: "ri-user-line" },
      { path: "/admin/onlinetest", label: "オンラインテスト", icon: "ri-file-list-3-line" },
      { path: "/admin/mail", label: "メール", icon: "ri-mail-line" },
      { path: "/admin/paymentmg", label: "入金管理", icon: "ri-bank-line" },
    ],
  },
  {
    id: "materials",
    title: "教材",
    items: [
      { path: "/admin/shikaku", label: "資格管理", icon: "ri-book-mark-line" },
      { path: "/admin/book-structure", label: "冊子構成管理", icon: "ri-book-2-line" },
      { path: "/admin/questions", label: "問題管理", icon: "ri-question-answer-line" },
      { path: "/admin/exam-sets", label: "試験セット管理", icon: "ri-git-branch-line" },
      { path: "/admin/quiz", label: "クイズ管理", icon: "ri-lightbulb-line" },
      { path: "/admin/sample-materials", label: "サンプル資料管理", icon: "ri-file-list-2-line" },
    ],
  },
  {
    id: "inventory",
    title: "在庫",
    items: [
      { path: "/admin/inventory", label: "在庫管理", icon: "ri-archive-2-line" },
      {
        path: "/admin/inventory-rule-sets",
        label: "在庫ルール条件",
        icon: "ri-filter-3-line",
      },
    ],
  },
  {
    id: "templates",
    title: "テンプレート",
    items: [
      { path: "/admin/templates/mail", label: "メールテンプレート管理", icon: "ri-mail-check-line" },
      { path: "/admin/templates/documents", label: "書類テンプレート管理", icon: "ri-file-paper-2-line" },
    ],
  },
  {
    id: "marketing",
    title: "マーケティング",
    items: [
      { path: "/admin/marketing/mailing-list", label: "メーリングリスト", icon: "ri-mail-send-line" },
      { path: "/admin/marketing/online-guide", label: "オンライン案内", icon: "ri-mail-open-line" },
    ],
  },
  {
    id: "analysis",
    title: "分析",
    items: [
      { path: "/admin/analysis", label: "分析", icon: "ri-bar-chart-grouped-line" },
      { path: "/admin/analysis/competitive", label: "競合分析", icon: "ri-scales-3-line" },
      { path: "/admin/analysis/competitive-mapping", label: "競合マッピング", icon: "ri-git-merge-line" },
      { path: "/admin/analysis/competitive-scrape", label: "競合情報取得設定", icon: "ri-download-cloud-2-line" },
    ],
  },
  {
    id: "other",
    title: "その他",
    items: [
      { path: "/admin/information", label: "お知らせ管理", icon: "ri-notification-3-line" },
      { path: "/admin/learning-restrictions", label: "学習制限管理", icon: "ri-shield-check-line" },
      { path: "/admin/settings", label: "設定", icon: "ri-settings-3-line" },
    ],
  },
];

const ALL_NAV_ITEMS = [DASHBOARD_ITEM, ...NAV_SECTIONS.flatMap((section) => section.items)];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, displayName } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    crm: true,
  });

  const currentTitle = useMemo(() => {
    const match = ALL_NAV_ITEMS.find(
      (item) => item.path !== DASHBOARD_ITEM.path && location.pathname.startsWith(item.path),
    );
    if (match) {
      return match.label;
    }
    if (
      location.pathname === DASHBOARD_ITEM.path ||
      location.pathname.startsWith("/admin/dashboard")
    ) {
      return DASHBOARD_ITEM.label;
    }
    return "Admin";
  }, [location.pathname]);

  const isDashboardActive =
    location.pathname === DASHBOARD_ITEM.path || location.pathname.startsWith("/admin/dashboard");

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const theme = await getAdminThemeSettings();
        applyAdminThemeSettingsToDocument(theme);
      } catch (error) {
        console.error("Error loading admin theme settings:", error);
      }
    };

    loadTheme();
  }, []);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-72 bg-white border-r border-gray-100 shadow-sm transform transition-transform duration-200 md:static md:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-6 border-b border-gray-100">
          <img src={logo} alt="Logo" className="h-8 w-auto rounded-md object-contain" />
          <div>
            <p className="text-sm text-gray-500">管理コンソール</p>
            <p className="text-lg font-semibold text-gray-900">Learning</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          <Link
            to={DASHBOARD_ITEM.path}
            onClick={() => setIsSidebarOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              isDashboardActive
                ? "bg-emerald-50 text-emerald-600 font-semibold"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          >
            <i
              className={`${DASHBOARD_ITEM.icon} text-lg ${
                isDashboardActive ? "text-emerald-600" : "text-gray-400"
              }`}
            />
            <span className="text-sm">{DASHBOARD_ITEM.label}</span>
          </Link>

          {NAV_SECTIONS.map((section) => {
            const isOpen = openSections[section.id] ?? false;
            return (
              <div key={section.id} className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
                >
                  <span>{section.title}</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = location.pathname.startsWith(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setIsSidebarOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                            isActive
                              ? "bg-emerald-50 text-emerald-600 font-semibold"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          <i
                            className={`${item.icon} text-lg ${
                              isActive ? "text-emerald-600" : "text-gray-400"
                            }`}
                          />
                          <span className="text-sm">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors mt-2"
          >
            <i className="ri-logout-box-line text-lg text-gray-400" />
            <span className="text-sm">ログアウト</span>
          </button>
          <p className="text-xs text-gray-400 text-right mt-1">v1.3.4</p>
        </div>
      </aside>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsSidebarOpen((open) => !open)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 md:hidden"
              aria-label="Toggle navigation"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">{currentTitle}</h1>
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <div className="relative hidden lg:block">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="検索"
                className="pl-10 pr-4 py-2 w-64 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all"
              />
            </div>

            <button
              type="button"
              className="relative p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <i className="ri-notification-3-line text-xl" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white" />
            </button>

            <div className="flex items-center gap-3 pl-4 border-l border-gray-100">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-semibold shadow-sm ring-2 ring-white">
                {(displayName || user?.email)?.[0]?.toUpperCase() ?? "A"}
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="text-sm font-semibold text-gray-900">{displayName || user?.email || "Admin"}</span>
                <span className="text-xs text-gray-500">Administrator</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
